import React from 'react';

/**
 * El botón de acción de una fila de tabla: solo icono, con el texto en el
 * tooltip.
 *
 * Existe por la misma razón que `Button`: cada listado se había inventado el
 * suyo. En "Otros pagos" eran iconos sueltos con `cursor-pointer` y ningún
 * `<button>` detrás —uno de ellos, el ojo, ni siquiera tenía `onClick`— y en
 * otras pantallas eran enlaces con el texto al lado, que hacen que cada fila
 * mida distinto según la acción que le toque y la columna salga desalineada.
 *
 * Dos cosas van encerradas aquí a propósito:
 *
 *  1. **Tamaño fijo**. Es lo que mantiene los iconos en la misma columna de una
 *     fila a otra aunque a una le falte una acción. Donde falte, se deja un
 *     hueco del mismo tamaño en vez de dejar que el resto se corra. El `md` son
 *     40px, que es el objetivo táctil mínimo recomendado; `sm` solo para filas
 *     apretadas donde no quepa.
 *  2. **En reposo todos son grises; el color sale al pasar por encima.** Una fila
 *     con un icono verde y otro rojo fijos parece que esté avisando de algo; el
 *     color es la consecuencia de la acción, y se enseña cuando vas a pulsarla.
 *     Por eso la variante solo define el `hover`, y la base no lleva ninguno: si
 *     lo llevara, serían dos utilidades de Tailwind duplicadas y cuál gana lo
 *     decide el orden de la hoja de estilos, no el de la cadena.
 *
 * El rojo de `danger` no se tiñe con la marca, igual que en `Button`: significa
 * "esto borra", y un centro con marca roja perdería la señal.
 */
export type IconButtonTone = 'neutral' | 'danger' | 'success' | 'brand';

const TONES: Record<IconButtonTone, string> = {
    neutral: 'hover:text-gray-800 hover:bg-gray-100',
    danger: 'hover:text-red-600 hover:bg-red-50',
    success: 'hover:text-emerald-600 hover:bg-emerald-50',
    brand: 'hover:text-blue-600 hover:bg-blue-50',
};

const SIZES = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
} as const;

export type IconButtonSize = keyof typeof SIZES;

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    tone?: IconButtonTone;
    size?: IconButtonSize;
    /** Obligatorio: es el tooltip Y el nombre accesible. Sin texto visible, sin
     *  esto el botón no se puede nombrar. */
    label: string;
}

export const IconButton: React.FC<IconButtonProps> = ({
    tone = 'neutral',
    size = 'md',
    label,
    className = '',
    children,
    ...props
}) => (
    <button
        type={props.type ?? 'button'}
        title={label}
        aria-label={label}
        className={[
            'inline-flex items-center justify-center rounded transition-colors text-gray-500',
            'disabled:opacity-40 disabled:cursor-not-allowed',
            'disabled:hover:bg-transparent disabled:hover:text-gray-500',
            SIZES[size],
            TONES[tone],
            className,
        ].filter(Boolean).join(' ')}
        {...props}
    >
        {children}
    </button>
);

/** El hueco que deja una acción que no aplica en esa fila, para que las demás
 *  no se corran de columna. */
export const IconButtonSlot: React.FC<{ size?: IconButtonSize }> = ({ size = 'md' }) => (
    <span className={SIZES[size]} aria-hidden="true" />
);

export default IconButton;
